/**
 * Users Page
 * User management, permissions, and audit log viewer
 */

import React, { useState } from 'react';
import { UsersIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { AuditLogViewer } from '../components/audit';
import { UserAssignmentManager } from '../components/users';
import { useAuth } from '../auth/AuthContext';

type TabType = 'users' | 'audit';

export const Users: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const { userRole } = useAuth();

  // Admin and Executives can access this page (handled by route protection)
  // But we double-check here for the audit tab
  const canViewAudit = userRole === 'Admin' || userRole === 'CEO' || userRole === 'Executive';

  const tabButtonClass = (tab: TabType) => {
    const base = 'px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors';
    if (activeTab === tab) {
      return `${base} border-blue-600 text-blue-600 bg-white`;
    }
    return `${base} border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <UsersIcon className="h-8 w-8 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
              <p className="text-gray-500">Manage users, permissions, and view audit logs</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 border-b border-gray-200">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('users')}
              className={tabButtonClass('users')}
            >
              <div className="flex items-center space-x-2">
                <UsersIcon className="h-4 w-4" />
                <span>Users</span>
              </div>
            </button>
            {canViewAudit && (
              <button
                onClick={() => setActiveTab('audit')}
                className={tabButtonClass('audit')}
              >
                <div className="flex items-center space-x-2">
                  <ClipboardDocumentListIcon className="h-4 w-4" />
                  <span>Audit Log</span>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'users' && (
            <UserAssignmentManager />
          )}

          {activeTab === 'audit' && canViewAudit && (
            <AuditLogViewer />
          )}
        </div>
      </div>
    </div>
  );
};
